import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, relTime, roleLabel, type Me, type Role } from '../lib/api'

/**
 * Team members.
 *
 * A company account is not one person. This is where the owner adds colleagues and gives
 * each the least role that lets them do their job — a developer who integrates the API
 * should not be able to remove the owner, and a viewer auditing deliverability should not
 * be able to send at all.
 */
export function Team({ me }: { me: Me }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('Developer')
  const [error, setError] = useState<ApiError | null>(null)
  const [created, setCreated] = useState<{ email: string; role: string } | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['team'], queryFn: api.team.list })

  const create = useMutation({
    mutationFn: () => api.team.create({ email: email.trim(), password, role }),
    onSuccess: async (r) => {
      setCreated({ email: r.email, role: r.role })
      setEmail(''); setPassword(''); setError(null)
      await qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: (e) => setError(e as ApiError),
  })

  const update = useMutation({
    mutationFn: (v: { id: string; role?: string; enabled?: boolean }) =>
      api.team.update(v.id, { role: v.role, enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
    onError: (e) => setError(e as ApiError),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.team.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
    onError: (e) => setError(e as ApiError),
  })

  const canManage = data?.your_role === 'Owner' || data?.your_role === 'Admin'
    || data?.your_role === 'SuperAdmin'
  const isOwnerLike = data?.your_role === 'Owner' || data?.your_role === 'SuperAdmin'

  // Admins cannot mint owners; only owner-likes can. Reflect that in the dropdown rather
  // than letting the server reject it after the fact.
  const assignableRoles: Role[] = isOwnerLike
    ? ['Owner', 'Admin', 'Developer', 'Viewer']
    : ['Admin', 'Developer', 'Viewer']

  return (
    <div>
      <h1>Team</h1>
      <p className="lede">
        {me.tenant.name} — everyone who can sign in to this account, and what each of them
        is allowed to do.
      </p>

      {canManage && (
        <div className="card" style={{ marginBottom: 24 }}>
          <form onSubmit={e => { e.preventDefault(); if (email.trim() && password) create.mutate() }}>
            <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
              <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
                <label htmlFor="te">Email</label>
                <input id="te" type="email" placeholder="colleague@company.com"
                       value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                <label htmlFor="tp">Temporary password</label>
                <input id="tp" type="text" placeholder="at least 12 characters"
                       value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <div className="field" style={{ flex: '0 1 150px', marginBottom: 0 }}>
                <label htmlFor="tr">Role</label>
                <select id="tr" value={role} onChange={e => setRole(e.target.value)}>
                  {assignableRoles.map(r => (
                    <option key={r} value={r}>{roleLabel[r]?.text ?? r}</option>
                  ))}
                </select>
              </div>
              <button type="submit" disabled={!email.trim() || password.length < 12 || create.isPending}>
                {create.isPending ? 'Adding…' : 'Add member'}
              </button>
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              {roleLabel[role]?.can}. They sign in with the password you set here — it is
              stored only as a hash, so share it securely and have them change it.
            </div>
          </form>

          {error && <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>{error.message}</div>}
          {created && (
            <div className="alert info reveal" style={{ marginTop: 12, marginBottom: 0 }}>
              <div>
                <strong>{created.email}</strong> added as {roleLabel[created.role]?.text ?? created.role}.
                They can sign in now at this dashboard.
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}

      {data && (
        <div className="card flush">
          <table className="data">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>MFA</th>
                <th>Last sign-in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.data.map(u => {
                const rl = roleLabel[u.role] ?? { text: u.role, cls: 'info', can: '' }
                const isSelf = u.email === data.your_email
                const targetIsOwner = u.role === 'Owner' || u.role === 'SuperAdmin'
                // An admin cannot act on an owner; only owner-likes can. And nobody edits
                // themselves here, to avoid locking yourself out mid-session.
                const editable = canManage && !isSelf && (isOwnerLike || !targetIsOwner)

                return (
                  <tr key={u.id} className={u.disabled_at ? 'dim' : ''}>
                    <td>
                      <div className="strong">{u.email}{isSelf && <span className="small muted"> · you</span>}</div>
                      <div className="small muted">{rl.can}</div>
                    </td>
                    <td>
                      {editable ? (
                        <select
                          value={u.role}
                          onChange={e => update.mutate({ id: u.id, role: e.target.value })}
                          className="inline-select"
                        >
                          {assignableRoles.map(r => (
                            <option key={r} value={r}>{roleLabel[r]?.text ?? r}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`badge ${rl.cls}`}>{rl.text}</span>
                      )}
                    </td>
                    <td>
                      {u.mfa_enabled
                        ? <span className="badge ok">On</span>
                        : <span className="small muted">Off</span>}
                    </td>
                    <td className="small muted">
                      {u.disabled_at
                        ? <span className="badge pending">Disabled</span>
                        : relTime(u.last_login_at)}
                    </td>
                    <td className="right">
                      {editable && (
                        u.disabled_at
                          ? <button className="ghost" onClick={() => update.mutate({ id: u.id, enabled: true })}>Re-enable</button>
                          : <button className="ghost danger" onClick={() => {
                              if (confirm(`Remove ${u.email}? They will be signed out and cannot sign back in.`)) {
                                remove.mutate(u.id)
                              }
                            }}>Remove</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <h2>What each role can do</h2>
      <div className="card flush">
        <table className="kv">
          <tbody>
            <tr><td>Super admin</td><td className="small">Full platform control. Reserved for the operator.</td></tr>
            <tr><td>Owner</td><td className="small">Everything, including managing owners and billing.</td></tr>
            <tr><td>Admin</td><td className="small">Domains, keys, suppressions, team — but cannot touch owners.</td></tr>
            <tr><td>Developer</td><td className="small">Create API keys and send. The integration role.</td></tr>
            <tr><td>Viewer</td><td className="small">Read-only across every screen. Changes nothing.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
